import React from 'react';
import {
  X, Calendar as CalendarIcon, MapPin, User, Phone, Video, Film,
  AlertCircle, Clock, ExternalLink, CheckCircle2,
} from 'lucide-react';
import {
  eventStatusBadgeClass, eventStatusLabel,
  noticeTierBadgeClass, noticeTierLabel,
  formatMediaDateRange,
} from '../../../lib/media';

// F1 — shared "all info for this event" modal. Opened from any role's calendar
// (or list) by clicking an event. Presentational: the caller passes the full
// serialized event + an optional array of action buttons.

function Row({ icon: Icon, label, children }) {
  if (children == null || children === '' || children === false) return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <Icon className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        <div className="text-[13px] text-gray-200 break-words">{children}</div>
      </div>
    </div>
  );
}

// `banner` ({ tone: 'muted'|'error', text }) shows a loading/error strip while
// a caller hydrates the full record; `submittedBy` is the "who asked, when"
// line for admin/ops views.
export default function MediaEventDetail({ event, onClose, actions = [], banner = null, submittedBy = null }) {
  if (!event) return null;
  const e = event;
  const assignee = e.assignedUserName
    || (e.externalFallbackName ? `${e.externalFallbackName} (external)` : null)
    || 'Unassigned';
  const delivered = [
    e.deliveredHighlightReel && 'Highlight reel',
    e.deliveredPhotoGallery && 'Photo gallery',
    e.deliveredRawVideos && 'Raw videos',
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06] sticky top-0 bg-[#0a0a0a]">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-white tracking-tight truncate">{e.eventTitle || 'Booking'}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${eventStatusBadgeClass(e.status)}`}>
                {eventStatusLabel(e.status)}
              </span>
              {e.noticeTier && e.noticeTier !== 'standard' && (
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${noticeTierBadgeClass(e.noticeTier)}`}>
                  {noticeTierLabel(e.noticeTier)}
                </span>
              )}
              {e.turnaround === 'rush' && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500/[0.1] border border-orange-400/30 text-orange-300">Rush</span>
              )}
              {e.approvalState === 'pending' && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/[0.1] border border-amber-400/30 text-amber-300">Awaiting approval</span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
        </div>

        {banner?.text && (
          <div className={`px-5 py-2 text-[11px] border-b border-white/[0.06] ${banner.tone === 'error' ? 'text-red-300 bg-red-500/[0.06]' : 'text-gray-500 bg-white/[0.02]'}`}
            data-testid="event-detail-banner">
            {banner.text}
          </div>
        )}
        <div className="px-5 py-3 divide-y divide-white/[0.04]">
          <div className="pb-1">
            <Row icon={CalendarIcon} label="When">{formatMediaDateRange(e.scheduledStartAt, e.scheduledEndAt)}</Row>
            {e.accountName && <Row icon={Film} label="School">{e.accountName}</Row>}
            {submittedBy && <Row icon={User} label="Requested by">{submittedBy}</Row>}
          </div>
          <div className="py-1">
            <Row icon={User} label="Shooter">{assignee}</Row>
            <Row icon={MapPin} label="Location">
              {[e.locationVenueName, e.locationAddress].filter(Boolean).join(' · ') || '—'}
              {e.locationOutsideNyc ? ' (outside NYC)' : ''}
            </Row>
          </div>
          {(e.primaryContactName || e.backupContactName) && (
            <div className="py-1">
              {e.primaryContactName && (
                <Row icon={Phone} label="Primary contact">
                  {e.primaryContactName}{e.primaryContactRole ? ` · ${e.primaryContactRole}` : ''}{e.primaryContactPhone ? ` · ${e.primaryContactPhone}` : ''}
                </Row>
              )}
              {e.backupContactName && (
                <Row icon={Phone} label="Backup contact">
                  {e.backupContactName}{e.backupContactPhone ? ` · ${e.backupContactPhone}` : ''}
                </Row>
              )}
            </div>
          )}
          {(e.eventDescription || e.priorityShots || e.parkingInstructions || e.equipmentNotes || e.eventCategory || e.tone) && (
            <div className="py-1">
              <Row icon={Film} label="Category / tone">{[e.eventCategory, e.tone].filter(Boolean).join(' · ') || null}</Row>
              <Row icon={AlertCircle} label="Priority shots">{e.priorityShots}</Row>
              <Row icon={AlertCircle} label="Description">{e.eventDescription}</Row>
              <Row icon={MapPin} label="Parking / arrival">{e.parkingInstructions}</Row>
              <Row icon={Film} label="Equipment notes">{e.equipmentNotes}</Row>
            </div>
          )}
          {Array.isArray(e.referenceUploads) && e.referenceUploads.length > 0 && (
            <div className="py-1">
              <Row icon={ExternalLink} label="Reference uploads">
                <ul className="space-y-0.5">
                  {e.referenceUploads.map((u, i) => {
                    const href = typeof u === 'string' ? u : (u?.url || u?.href);
                    const name = typeof u === 'string' ? u : (u?.name || u?.filename || href);
                    return (
                      <li key={i}>
                        {href ? <a href={href} target="_blank" rel="noreferrer" className="text-[#B0FC38] hover:underline">{name}</a> : name}
                      </li>
                    );
                  })}
                </ul>
              </Row>
            </div>
          )}
          {(e.premeetingMeetUrl || e.premeetingScheduledAt) && (
            <div className="py-1">
              <Row icon={Video} label="Pre-event meeting">
                {e.premeetingScheduledAt ? formatMediaDateRange(e.premeetingScheduledAt, e.premeetingScheduledAt) : 'Scheduled'}
                {e.premeetingMeetUrl && (
                  <a href={e.premeetingMeetUrl} target="_blank" rel="noreferrer" className="ml-2 text-[#B0FC38] hover:underline inline-flex items-center gap-1">
                    Join <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </Row>
            </div>
          )}
          {(e.driveFolderUrl || delivered.length > 0 || e.deliveredAt) && (
            <div className="py-1">
              <Row icon={CheckCircle2} label="Delivered">
                {delivered.length ? delivered.join(', ') : (e.deliveredAt ? 'Yes' : null)}
                {e.driveFolderUrl && (
                  <a href={e.driveFolderUrl} target="_blank" rel="noreferrer" className="ml-2 text-[#B0FC38] hover:underline inline-flex items-center gap-1">
                    Drive folder <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </Row>
            </div>
          )}
          {e.overageMinutes > 0 && (
            <div className="py-1">
              <Row icon={Clock} label="Overage">
                {Math.round(e.overageMinutes)} min over{e.overageDecision ? ` · ${e.overageDecision}` : ' · awaiting review'}
              </Row>
            </div>
          )}
        </div>

        {actions.length > 0 && (
          <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-end gap-2 flex-wrap sticky bottom-0 bg-[#0a0a0a]">
            {actions.map((a) => (
              <button key={a.label} type="button" onClick={a.onClick}
                className={`px-3.5 py-2 rounded-full text-[12px] font-medium ${
                  a.variant === 'danger' ? 'text-red-300 border border-red-500/30 hover:bg-red-500/10'
                    : a.variant === 'primary' ? 'bg-[#B0FC38] text-gray-900 hover:bg-[#9BE22E]'
                    : 'text-gray-300 border border-white/[0.1] hover:text-white hover:border-white/[0.2]'}`}>
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
